import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { filterNonExpiryProducts } from '../utils/formatters';

// Types (Consider moving shared types to a dedicated file)
type ClusterOption = {
  id: string;
  name: string;
};

type Product = {
  id: string;
  name: string;
  cost: number;
  category: string; // Useful for filtering if needed
};

// Add type for My Requests
interface MyRequest {
  id: string;
  request_type: string;
  status: string;
  request_details: any;
  created_at: string;
  updated_at: string;
}

const ACTIVATION_COST_PER_SIM = 15;
const FLOAT_PERCENTAGE = 0.20;

export function RequestSims() {
  const { profile } = useAuth();
  const organizationId = profile?.organization_id;
  const userId = profile?.id; // Get user ID

  // Form State
  const [selectedClusterId, setSelectedClusterId] = useState<string>('');
  const [newClusterName, setNewClusterName] = useState<string>(''); // State for new cluster name
  const [numSims, setNumSims] = useState<number | string>('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');

  // Data State
  const [clusters, setClusters] = useState<ClusterOption[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [filterExpiryProducts, setFilterExpiryProducts] = useState<boolean>(true);

  // Control State
  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // State for user's submitted requests
  const [myRequests, setMyRequests] = useState<MyRequest[]>([]);
  const [loadingMyRequests, setLoadingMyRequests] = useState(true);
  const [errorMyRequests, setErrorMyRequests] = useState<string | null>(null);

  // Fetch initial data (clusters, products, balance)
  const fetchInitialData = useCallback(async () => {
    if (!organizationId) {
      setError("Organization context not found.");
      setLoadingData(false);
      return;
    }
    setLoadingData(true);
    setError(null);
    try {
      const [clusterRes, productRes] = await Promise.all([
        supabase
          .from('clusters')
          .select('id, name')
          .eq('organization_id', organizationId)
          .order('name'),
        supabase
          .from('products')
          .select('id, name, cost, category')
          .eq('is_active', true)
          .order('cost'),
      ]);

      // Fetch organization details (balance, name, and filter setting)
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('wallet_balance, name, filter_expiry_products')
        .eq('id', organizationId)
        .single();

      if (clusterRes.error) throw clusterRes.error;
      setClusters(clusterRes.data || []);

      if (productRes.error) throw productRes.error;
      setProducts(productRes.data || []);

      if (orgError) throw orgError;
      setWalletBalance(orgData?.wallet_balance ?? 0);
      setOrganizationName(orgData?.name || '');
      setFilterExpiryProducts(orgData?.filter_expiry_products ?? true);

    } catch (err: any) {
      console.error("Error fetching initial data:", err);
      setError(err.message || 'Failed to load required data.');
      setClusters([]);
      setProducts([]);
      setWalletBalance(null);
      setOrganizationName('');
      setFilterExpiryProducts(true);
    } finally {
      setLoadingData(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Fetch user's submitted requests
  const fetchMyRequests = useCallback(async () => {
    if (!userId) return; // Don't fetch if user ID isn't available

    setLoadingMyRequests(true);
    setErrorMyRequests(null);
    try {
      const { data, error } = await supabase
        .from('admin_requests')
        .select('id, request_type, status, request_details, created_at, updated_at')
        .eq('requesting_user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10); // Limit for performance, add pagination later if needed

      if (error) throw error;
      setMyRequests(data || []);
    } catch (err: any) {
      console.error("Error fetching user requests:", err);
      setErrorMyRequests(err.message || 'Failed to load your requests.');
      setMyRequests([]);
    } finally {
      setLoadingMyRequests(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchMyRequests();
  }, [fetchMyRequests]);

  // --- Calculation Logic --- 
  const selectedProduct = useMemo(() => 
    products.find(p => p.id === selectedProductId)
  , [products, selectedProductId]);

  const numericNumSims = typeof numSims === 'number' ? numSims : parseInt(String(numSims), 10);
  const isValidNumSims = !isNaN(numericNumSims) && numericNumSims > 0;

  const activationCost = isValidNumSims ? numericNumSims * ACTIVATION_COST_PER_SIM : 0;
  const initialProductCostTotal = isValidNumSims && selectedProduct ? 
    numericNumSims * selectedProduct.cost : 0;
  const totalSetupCost = activationCost + initialProductCostTotal;
  const recommendedFloat = initialProductCostTotal * FLOAT_PERCENTAGE;

  // --- Handlers --- 
  const handleClusterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as string;
    setSelectedClusterId(value);
    if (value !== '_CREATE_NEW_') {
        setNewClusterName(''); // Clear new name if existing cluster is selected
    }
  };

  const handleNumSimsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow empty string or positive integers
    if (value === '' || /^[1-9]\d*$/.test(value)) {
        setNumSims(value === '' ? '' : parseInt(value, 10));
    } else if (value === '0') {
        setNumSims(''); // Treat 0 as empty
    }
  };

  const handleProductChange = (event: SelectChangeEvent<string>) => {
    setSelectedProductId(event.target.value as string);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // Add check for new cluster name if create is selected
    if (selectedClusterId === '_CREATE_NEW_' && !newClusterName.trim()) {
      setError('Please enter a name for the new cluster.');
      return;
    }

    if (!selectedClusterId || !isValidNumSims || !selectedProductId || !selectedProduct || !organizationId || !profile?.id ) {
        setError('Missing required information (ensure cluster/new name, SIMs, and product are selected).');
        return;
    }

    setSubmitting(true);
    let clusterIdToUse = selectedClusterId; 
    let clusterNameToUse = clusters.find(c => c.id === selectedClusterId)?.name;

    try {
        // --- Create Cluster if requested --- 
        if (selectedClusterId === '_CREATE_NEW_') {
          console.log(`Attempting to create new cluster: ${newClusterName}`);
          const { data: newClusterData, error: clusterInsertError } = await supabase
            .from('clusters')
            .insert({ 
              name: newClusterName.trim(), 
              organization_id: organizationId,
              use_case_type: 'other', // Provide a default value
              // Add other required fields for cluster if any (e.g., default rule settings)
              // rules: [], // Example default
            })
            .select('id, name') // Select ID and name to use later
            .single(); // Expecting a single row back

          if (clusterInsertError) {
            throw new Error(`Failed to create cluster: ${clusterInsertError.message}`);
          }
          
          if (!newClusterData?.id) {
            throw new Error('Failed to retrieve ID for newly created cluster.');
          }

          console.log(`Cluster created successfully: ID ${newClusterData.id}`);
          clusterIdToUse = newClusterData.id; // Use the new ID
          clusterNameToUse = newClusterData.name; // Use the new name

          // Optional: Refresh cluster list in background, though not strictly needed for this submission
          // fetchInitialData(); // Or a more targeted fetchClusters
        }

        // --- Prepare and Submit Request --- 
        const requestDetailsForDb = {
            requestingOrgName: organizationName,
            clusterId: clusterIdToUse, // Use the determined ID
            clusterName: clusterNameToUse, // Use the determined name
            didCreateNewCluster: selectedClusterId === '_CREATE_NEW_', // Add flag for context
            numSims: numericNumSims,
            productId: selectedProductId,
            productName: selectedProduct.name,
            productCost: selectedProduct.cost,
            activationCost: activationCost,
            initialProductCostTotal: initialProductCostTotal,
            totalSetupCost: totalSetupCost,
            recommendedFloat: recommendedFloat,
        };

        const requestData = {
            organization_id: organizationId,
            requesting_user_id: profile.id,
            request_type: 'bulk_sim_upload',
            status: 'pending',
            request_details: requestDetailsForDb, 
        };

        const { error: insertError } = await supabase
            .from('admin_requests')
            .insert(requestData);

        if (insertError) throw insertError;

        setSuccessMessage(`Request for ${numericNumSims} SIM(s) for cluster '${clusterNameToUse}' submitted for approval.`);
        // Reset form 
        setSelectedClusterId('');
        setNewClusterName(''); // Reset new cluster name
        setNumSims('');
        setSelectedProductId('');
        
        fetchMyRequests(); 

    } catch (err: any) {
        console.error("Error submitting SIM request for approval:", err);
        setError(err.message || 'Failed to submit request for approval.');
    } finally {
        setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'R-.--';
    return `R${amount.toFixed(2)}`;
  };

  // Helper to format details concisely
  const formatRequestDetails = (details: any, type: string): string => {
    if (!details) return 'N/A';
    try {
      if (type === 'bulk_sim_upload' && details.numSims) {
        return `${details.numSims} SIMs for cluster ${details.clusterName || 'N/A'}`;
      } else if (type === 'wallet_topup' && details.amount) {
        return `Top-up R${details.amount}`;
      } else if (details.genericDetails) {
        return details.genericDetails.substring(0, 50) + (details.genericDetails.length > 50 ? '...' : '');
      }
      // Fallback for unknown structure
      return JSON.stringify(details).substring(0, 50) + (JSON.stringify(details).length > 50 ? '...' : '');
    } catch { 
      return 'Invalid details format';
    }
  };
  
  // Helper for status chip color
  const getStatusChipColor = (status: string): "success" | "warning" | "error" | "info" | "default" => {
    switch (status.toLowerCase()) {
      case 'approved':
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Request New SIMs
      </Typography>

      {loadingData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : error && !submitting ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              {/* Left Side - Form Inputs */}
              <Grid size={{ xs: 12, md: 7 }}>
                 <Typography variant="h6" gutterBottom>Request Details</Typography>
                  <FormControl fullWidth margin="normal" required>
                    <InputLabel id="cluster-select-label">Target Cluster</InputLabel>
                    <Select
                      labelId="cluster-select-label"
                      value={selectedClusterId}
                      label="Target Cluster"
                      onChange={handleClusterChange}
                      disabled={clusters.length === 0 && selectedClusterId !== '_CREATE_NEW_'} // Allow create even if loading fails
                    >
                      <MenuItem value="" disabled><em>Select Cluster...</em></MenuItem>
                      {clusters.map((cluster) => (
                        <MenuItem key={cluster.id} value={cluster.id}>{cluster.name}</MenuItem>
                      ))}
                      {/* Add Create New Option */} 
                      <MenuItem value="_CREATE_NEW_">-- Create New Cluster --</MenuItem>
                    </Select>
                  </FormControl>

                 {/* Conditionally show New Cluster Name field */} 
                 {selectedClusterId === '_CREATE_NEW_' && (
                    <TextField
                        required
                        fullWidth
                        margin="normal"
                        id="newClusterName"
                        name="newClusterName"
                        label="New Cluster Name"
                        value={newClusterName}
                        onChange={(e) => setNewClusterName(e.target.value)}
                        error={!newClusterName.trim()} // Basic validation
                        helperText={!newClusterName.trim() ? "New cluster name cannot be empty" : ""}
                    />
                 )}

                 <TextField
                    required
                    fullWidth
                    margin="normal"
                    id="numSims"
                    name="numSims"
                    label="Number of SIMs Required"
                    type="number" // Use text for controlled input, validate as number
                    inputProps={{ min: 1, step: 1 }}
                    value={numSims}
                    onChange={handleNumSimsChange}
                    error={numSims !== '' && !isValidNumSims}
                    helperText={numSims !== '' && !isValidNumSims ? "Please enter a valid number (1 or more)" : ""}
                  />

                  <FormControl fullWidth margin="normal" required>
                    <InputLabel id="product-select-label">Initial Product</InputLabel>
                    <Select
                      labelId="product-select-label"
                      value={selectedProductId}
                      label="Initial Product"
                      onChange={handleProductChange}
                      disabled={products.length === 0}
                    >
                      <MenuItem value="" disabled><em>Select Product...</em></MenuItem>
                      {filterNonExpiryProducts(products, filterExpiryProducts).map((product) => (
                        <MenuItem key={product.id} value={product.id}>
                           {`${product.name} (${formatCurrency(product.cost)})`}
                        </MenuItem>
                      ))}
                       {products.length === 0 && <MenuItem disabled>No products found.</MenuItem>}
                    </Select>
                  </FormControl>
              </Grid>

              {/* Right Side - Cost Summary */}
              <Grid size={{ xs: 12, md: 5 }}>
                  <Typography variant="h6" gutterBottom>Cost Estimate & Wallet</Typography>
                  <Box sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                     <Typography variant="body2">SIM Activation Cost:</Typography>
                     <Typography variant="h6" gutterBottom>{formatCurrency(activationCost)}</Typography>

                     <Typography variant="body2">Initial Product Cost:</Typography>
                     <Typography variant="h6" gutterBottom>{formatCurrency(initialProductCostTotal)}</Typography>

                     <Divider sx={{ my: 1 }} />

                     <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total Setup Cost:</Typography>
                     <Typography variant="h5" color="primary" gutterBottom>{formatCurrency(totalSetupCost)}</Typography>

                     <Divider sx={{ my: 1 }} />

                     <Typography variant="body2" sx={{ mt: 2 }}>Current Wallet Balance:</Typography>
                     <Typography variant="body1" gutterBottom>{formatCurrency(walletBalance)}</Typography>
                     
                      {(walletBalance !== null && totalSetupCost > walletBalance) && (
                        <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                          Your current balance is less than the estimated setup cost.
                        </Alert>
                      )}

                     <Typography variant="body2" sx={{ mt: 2 }}>Recommended Float:</Typography>
                     <Typography variant="body1" gutterBottom>{formatCurrency(recommendedFloat)}</Typography>
                     <Typography variant="caption" display="block">
                       *This float helps cover future auto top-ups based on your initial product selection. Actual needs may vary.
                     </Typography>
                  </Box>
              </Grid>

               {/* Bottom Area - Messages & Submit */} 
              <Grid size={12}>
                 {error && !submitting && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                 )}
                 {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>
                 )}
              </Grid>
              <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                    type="submit" 
                    variant="contained" 
                    disabled={submitting || loadingData || !selectedClusterId || !isValidNumSims || !selectedProductId}
                    sx={{ minWidth: 150 }}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Submit Request'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}

      {/* --- My Requests Section --- */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          My Recent Requests
        </Typography>
        {loadingMyRequests ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : errorMyRequests ? (
          <Alert severity="error">{errorMyRequests}</Alert>
        ) : myRequests.length === 0 ? (
          <Typography>You haven't submitted any requests recently.</Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label="my requests table">
              <TableHead>
                <TableRow>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {myRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{format(new Date(request.created_at), 'yyyy-MM-dd HH:mm')}</TableCell>
                    <TableCell>{request.request_type.replace(/_/g, ' ').toUpperCase()}</TableCell>
                    <TableCell>{formatRequestDetails(request.request_details, request.request_type)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={request.status.toUpperCase()} 
                        size="small"
                        color={getStatusChipColor(request.status)}
                      />
                    </TableCell>
                    <TableCell>{format(new Date(request.updated_at), 'yyyy-MM-dd HH:mm')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
} 